import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, RefreshCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { getCaptcha, submitFeedback } from "../lib/api";

export default function FeedbackButton({ variant = "fab" }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [captcha, setCaptcha] = useState({ token: "", question: "" });
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const c = await getCaptcha();
      setCaptcha(c);
      setAnswer("");
    } catch {
      toast.error("Could not load captcha");
    }
  };

  useEffect(() => {
    if (open && !captcha.token) {
      refresh();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || !answer.trim() || !captcha.token) return;
    setBusy(true);
    try {
      await submitFeedback({
        message,
        name: name || undefined,
        email: email || undefined,
        captcha_token: captcha.token,
        captcha_answer: answer,
      });
      toast.success("Feedback sent", {
        description: "Thank you - this is how the next feature gets prioritized.",
      });
      setName("");
      setEmail("");
      setMessage("");
      setAnswer("");
      setCaptcha({ token: "", question: "" });
      setOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 403) {
        toast.error("Captcha was wrong", { description: "Try the new question." });
        refresh();
      } else if (status === 400) {
        toast.error("Invalid feedback", { description: String(detail) });
      } else if (status !== 429) {
        toast.error("Could not send", { description: String(detail) });
      }
    } finally {
      setBusy(false);
    }
  };

  const triggerClass =
    variant === "header"
      ? "flex items-center gap-1.5 text-xs font-mono text-violet-700 hover:text-violet-800 border border-violet-200 hover:border-violet-300 bg-violet-50 hover:bg-violet-100 rounded-md px-3 py-1.5 transition-colors"
      : "fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-300 hover:border-violet-300 rounded-full px-4 py-2.5 text-sm font-medium text-slate-800 hover:text-violet-700 shadow-md transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={variant === "header" ? "feedback-header-btn" : "feedback-fab"}
          aria-label="Send feedback"
          className={triggerClass}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          <span className={variant === "header" ? "hidden sm:inline" : "hidden sm:inline"}>
            Feedback
          </span>
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 sm:max-w-md"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Send feedback
          </DialogTitle>
          <DialogDescription className="text-slate-600 text-sm">
            What worked? What was wrong? What would you build next? One human reads every note.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Name (optional)</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                data-testid="fb-name"
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-xs text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Email (optional)</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                maxLength={200}
                data-testid="fb-email"
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-xs text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-1.5">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              minLength={5}
              maxLength={2000}
              required
              rows={5}
              placeholder="The exploit-difficulty widget would be even better if..."
              data-testid="fb-message"
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-xs text-slate-800 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200 resize-y"
            />
            <div className="text-[10px] font-mono text-slate-500 mt-1 text-right">
              {message.length} / 2000
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="overline">Quick captcha</span>
              <button
                type="button"
                onClick={refresh}
                title="New question"
                data-testid="fb-captcha-refresh"
                className="text-slate-500 hover:text-violet-700 transition-colors"
              >
                <RefreshCcw className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span
                data-testid="fb-captcha-question"
                className="font-mono text-sm text-violet-700 flex-1"
              >
                {captcha.question || "Loading captcha..."}
              </span>
              <input
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                required
                inputMode="numeric"
                maxLength={6}
                data-testid="fb-captcha-answer"
                placeholder="?"
                className="w-20 text-center bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 font-mono text-sm text-slate-900 focus:outline-none focus:border-violet-600"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-mono text-slate-600 hover:text-slate-800 border border-slate-200 rounded-md px-3 py-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !message.trim() || !answer.trim()}
              data-testid="fb-submit"
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-3.5 w-3.5" />
              {busy ? "Sending..." : "Send feedback"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
